import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCartIcon, UserIcon } from '@heroicons/react/24/outline';
import useAuthStore from '../store/authStore';

const Navbar = ({ customClass, hideRightMenu, hideLogo }) => {
  const { user, userType, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className={`w-full z-50 transition-all duration-300 ${customClass || (scrolled ? 'fixed top-0 bg-white/70 backdrop-blur-md shadow-lg border-b border-white/20' : 'fixed top-0 bg-transparent')}`}>
      {/* Standard width container with padding, slightly wider on Home for "little bit left" feel without edge touching */}
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${isHome ? 'max-w-full px-6 md:px-12' : 'max-w-7xl'}`}>
        <div className="flex justify-between h-auto py-2 items-start">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 group z-[100]">
              {/* Logo: Clean positioning, no negative margin to avoid hiding */}
              {!hideLogo && (
                <div className="relative">
                  {/* Logo Glow Effect */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-teal-400 rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-200"></div>
                  <img
                    src="/images/logo.png"
                    alt="Good Buy Logo"
                    className="relative w-auto bg-white/90 backdrop-blur-sm rounded-lg p-1 shadow-sm border border-white/20 h-[80px] md:h-[90px]"
                  />
                </div>
              )}
            </Link>

            {/* Desktop Navigation for Authenticated Users */}
            <div className="hidden md:block ml-12 flex items-baseline space-x-6 mt-4">
              {userType === 'supplier' && (
                <>
                  <NavLink to="/supplier/dashboard" scrolled={scrolled}>Dashboard</NavLink>
                  {/* DISABLED: My Products tab — managed via registration profile */}
                  {/* <NavLink to="/supplier/products" scrolled={scrolled}>My Products</NavLink> */}
                  {/* DISABLED: Orders tab — coming soon */}
                  {/* <NavLink to="/supplier/orders" scrolled={scrolled}>Orders</NavLink> */}
                </>
              )}
              {(userType === 'admin' || userType === 'superadmin') && (
                <>
                  <NavLink to="/admin/dashboard" scrolled={scrolled}>Dashboard</NavLink>
                  <NavLink to="/admin/suppliers" scrolled={scrolled}>Suppliers</NavLink>
                  <NavLink to="/admin/users" scrolled={scrolled}>Users</NavLink>
                  {/* DISABLED: Orders tab — removed as per requirement */}
                  {/* <NavLink to="/admin/orders" scrolled={scrolled}>Orders</NavLink> */}
                  <NavLink to="/admin/sliders" scrolled={scrolled}>Sliders</NavLink>
                  <NavLink to="/admin/advertisements" scrolled={scrolled}>Ads</NavLink>
                </>
              )}
            </div>
          </div>

          {/* Right Menu: Hidden if requested (e.g., on Home Page where it's in Top Bar) */}
          {!hideRightMenu && (
            <div className="flex items-center space-x-6">
              {/* Guest Navigation: About & Login */}
              {!user && (
                <>
                  <NavLink to="/" scrolled={scrolled} isHome={isHome}>Home</NavLink>
                </>
              )}

              {/* Authenticated User Icons/Menu */}
              {userType === 'user' && (
                <Link to="/cart" className={`transition-colors duration-200 ${scrolled ? 'text-gray-700 hover:text-blue-600' : 'text-gray-100 hover:text-white'}`}>
                  <ShoppingCartIcon className="h-6 w-6" />
                </Link>
              )}

              {user ? (
                <div className="relative group">
                  <button className={`flex items-center space-x-2 font-medium transition-colors duration-200 ${scrolled ? 'text-gray-700 hover:text-blue-600' : 'text-gray-100 hover:text-white'}`}>
                    <UserIcon className="h-6 w-6" />
                    <span>{user.name || user.businessName || user.phone}</span>
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white/90 backdrop-blur-xl rounded-xl shadow-2xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top-right scale-95 group-hover:scale-100 border border-white/20 z-50">
                    {userType === 'user' && (
                      <Link to="/orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">My Orders</Link>
                    )}
                    <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">Logout</button>
                  </div>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="px-6 py-1.5 rounded-full font-bold text-xs uppercase tracking-wide transition-all duration-300 shadow-md hover:shadow-cyan-500/30 bg-blue-600 text-white"
                >
                  Log in
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

// Helper component for links
const NavLink = ({ to, children, scrolled, isHome }) => (
  <Link
    to={to}
    className={`px-3 py-2 rounded-md text-sm font-bold uppercase tracking-widest transition-all duration-200 ${scrolled
      ? 'text-gray-600 hover:text-amber-600 hover:bg-amber-50/50'
      : (isHome ? 'text-gray-400 hover:text-amber-500' : 'text-gray-100 hover:text-white hover:bg-white/10')
      }`}
  >
    {children}
  </Link>
);

export default Navbar;